import React, { useState, useEffect } from "react";
import { Phone, X } from "lucide-react";
import { useSite } from "../context/SiteContext";

// Floating "Call us" button — bottom-right on every public page.
// Mobile: tap-to-call. Desktop: opens a small card showing the number + click-to-copy.
export default function CallButton() {
  const { site } = useSite();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const phone = site?.phone || "203-437-8193";
  const phone2 = site?.phone2 || "";
  const telHref = `tel:${phone.replace(/[^\d+]/g, "")}`;
  const telHref2 = phone2 ? `tel:${phone2.replace(/[^\d+]/g, "")}` : "";

  // On mobile we just use a direct tel: link. Detect via media query.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  // Mobile: single tap fires the phone app
  if (isMobile) {
    return (
      <a
        href={telHref}
        data-testid="call-fab"
        aria-label={`Call ${phone}`}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 px-5 py-3.5 bg-[#3A5A40] text-[#FDFBF7] shadow-lg hover:bg-[#2C4C3B] transition-colors print:hidden"
        style={{ borderRadius: "999px" }}
      >
        <Phone className="w-4 h-4" />
        <span className="text-sm font-medium tracking-wide">Call us</span>
      </a>
    );
  }

  // Desktop: button opens a small luxury card with the number + copy/call actions
  return (
    <div className="fixed bottom-6 right-6 z-50 print:hidden" data-testid="call-fab-wrap">
      {open && (
        <div
          data-testid="call-card"
          className="bg-[#FDFBF7] border border-[#E2DCD0] shadow-2xl mb-3 overflow-hidden"
          style={{ width: "18rem" }}
        >
          <div className="bg-[#3A5A40] text-[#FDFBF7] flex items-center justify-between px-4 py-3">
            <p className="text-xs uppercase tracking-[0.28em]">Office</p>
            <button
              onClick={() => setOpen(false)}
              data-testid="call-card-close"
              aria-label="Close"
              className="opacity-80 hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-5 py-5">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#57534E] mb-1">Office</p>
            <p className="font-serif text-2xl text-[#1C1917] tracking-tight" data-testid="call-card-phone">
              {phone}
            </p>
            {phone2 ? (
              <>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#57534E] mt-3 mb-1">Cell</p>
                <a
                  href={telHref2}
                  data-testid="call-card-phone2"
                  className="block font-serif text-lg text-[#3A5A40] tracking-tight hover:underline"
                >
                  {phone2}
                </a>
              </>
            ) : null}
            <p className="text-xs text-[#57534E] mt-2 leading-relaxed">
              Mon–Fri · 8am — 8pm · Sat · 9am — 4pm.<br />
              Same-day callbacks for new patients.
            </p>
            <div className="flex gap-2 mt-4">
              <a
                href={telHref}
                data-testid="call-card-dial"
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#2C4C3B] transition-colors text-xs uppercase tracking-[0.22em]"
              >
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
              <button
                onClick={copy}
                data-testid="call-card-copy"
                className="flex-1 inline-flex items-center justify-center px-3 py-2.5 border border-[#E2DCD0] text-[#1C1917] hover:bg-[#F4F1EA] transition-colors text-xs uppercase tracking-[0.22em]"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="call-fab"
        aria-label="Call us"
        className="inline-flex items-center gap-2 px-5 py-3.5 bg-[#3A5A40] text-[#FDFBF7] shadow-lg hover:bg-[#2C4C3B] transition-colors"
        style={{ borderRadius: "999px" }}
      >
        <Phone className="w-4 h-4" />
        <span className="text-sm font-medium tracking-wide">Call us</span>
      </button>
    </div>
  );
}
